/**
 * CollectEaze browser softphone.
 *
 * Adapted from the Call Engine console's softphone.js. The agent's phone,
 * living inside the dashboard: Django issues a temporary SIP credential
 * (POST /dialer/softphone/session/) and this registers with the Call Engine
 * over WSS, rings when the engine calls the agent leg, plays the remote audio
 * and exposes answer, mute, DTMF and hang-up.
 *
 * ## What this deliberately does not do
 *
 * It does not dial, and it makes no REST calls. Calls are created through
 * Django (POST /dialer/calls/initiate/), which has the engine ring this
 * endpoint and then dial the borrower; hold, transfer, participants and the
 * normal hang-up also go through Django. Only SIP signalling and audio go
 * to the Call Engine, over the websocket_url Django returned.
 *
 * ## Credentials
 *
 * The SIP password lives only on this object, for as long as the page does.
 * It is never put in React state, Redux, storage or logs - anything that
 * outlives the page or can be read by other code would leak a working phone
 * line. disconnect() drops it.
 *
 * ## Re-registration
 *
 * Asterisk expires a contact roughly every ten minutes. Renewing after
 * expiry leaves a gap where an incoming call reaches nothing, so this renews
 * early, and treats a lost socket as an outage worth telling the agent about.
 */

const RE_REGISTER_MARGIN_MS = 120000; // renew two minutes early
// Relay credentials are refreshed well before they expire: a call placed
// just after expiry allocates no relay and the agent hears nothing.
const ICE_REFRESH_MARGIN_MS = 600000; // ten minutes early
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const STATS_INTERVAL_MS = 2000;
// Above this packet loss, audio is audibly broken.
const POOR_AUDIO_LOSS_RATIO = 0.05;
const AUDIO_ONLY = { audio: true, video: false };

export const PhoneState = {
  OFFLINE: "offline",
  CONNECTING: "connecting",
  READY: "ready",
  RINGING: "ringing",
  ON_CALL: "on_call",
  FAILED: "failed",
};

const required = (value, name) => {
  if (!value) throw new Error(`softphone: ${name} is required`);
  return value;
};

export default class Softphone {
  /**
   * @param SIP     the SIP.js namespace (see loadSip)
   * @param options onState(state), onCall({ event, ... }), onError(message),
   *                onQuality({ lossRatio, poor }), onIceExpiring(),
   *                autoAnswer (answer the engine's ring without a click)
   */
  constructor(SIP, options = {}) {
    this.SIP = required(SIP, "SIP");
    this.onState = options.onState || (() => {});
    this.onCall = options.onCall || (() => {});
    this.onError = options.onError || (() => {});
    this.onQuality = options.onQuality || (() => {});
    // Called when the TURN credential is about to expire. The dashboard
    // fetches GET /dialer/softphone/ice-servers/ and hands the result to
    // setIceServers. Without it, relays stop working mid-shift.
    this.onIceExpiring = options.onIceExpiring || null;
    this.autoAnswer = !!options.autoAnswer;

    this._ua = null;
    this._registerer = null;
    this._session = null;
    this._state = PhoneState.OFFLINE;
    this._credential = null;
    this._reconnectDelay = RECONNECT_BASE_MS;
    this._reconnectTimer = null;
    this._registerTimer = null;
    this._statsTimer = null;
    this._iceTimer = null;
    this._sdhOptions = null;
    this._stopping = false;
    this._audio = null;
    this._muted = false;
  }

  get state() {
    return this._state;
  }

  get muted() {
    return this._muted;
  }

  _setState(next) {
    if (this._state === next) return;
    this._state = next;
    this.onState(next);
  }

  _fail(message) {
    this.onError(message);
  }

  /**
   * Connect with the credential from POST /dialer/softphone/session/:
   *   { sip_username, password, realm, websocket_url, ice_servers, ice_expires_at }
   */
  async connect(credential) {
    required(credential, "credential");
    required(credential.sip_username, "sip_username");
    required(credential.password, "password");
    required(credential.websocket_url, "websocket_url");

    await this._teardownUa();
    this._credential = credential;
    this._stopping = false;
    this._reconnectDelay = RECONNECT_BASE_MS;
    await this._open();
    this._scheduleIceRefresh(credential.ice_expires_at);
  }

  async _open() {
    const SIP = this.SIP;
    const cred = this._credential;
    if (!cred) return;

    this._setState(PhoneState.CONNECTING);
    const server = cred.websocket_url;

    try {
      const host = new URL(server).hostname;

      // Held by reference so setIceServers can replace the relay
      // credential in place. SIP.js reads this object when it builds each
      // session's peer connection, so mutating it takes effect on the next
      // call without tearing down the registration.
      this._sdhOptions = {
        peerConnectionConfiguration: {
          iceServers: (cred.ice_servers || []).slice(),
          rtcpMuxPolicy: "require",
        },
        constraints: AUDIO_ONLY,
      };

      if (!(cred.ice_servers || []).length) {
        // Not fatal on a LAN with the PBX. Off it, a symmetric NAT gives
        // one-way audio, which is otherwise indistinguishable from a bad line.
        this._fail("No relay configured. Audio may not work on some networks.");
      }

      this._ua = new SIP.UserAgent({
        uri: SIP.UserAgent.makeURI(`sip:${cred.sip_username}@${host}`),
        authorizationUsername: cred.sip_username,
        authorizationPassword: cred.password,
        transportOptions: { server },
        sessionDescriptionHandlerFactoryOptions: this._sdhOptions,
        delegate: {
          onInvite: (invitation) => this._receive(invitation),
          onDisconnect: (error) => this._dropped(error),
        },
      });
    } catch (err) {
      this._setState(PhoneState.FAILED);
      this._fail("Could not start the phone. The phone server address is invalid.");
      return;
    }

    try {
      await this._ua.start();
      this._registerer = new SIP.Registerer(this._ua, { expires: 600 });
      this._registerer.stateChange.addListener((s) => this._registration(s));
      await this._registerer.register({
        requestDelegate: {
          // Wrong or rotated password: retrying with the same credential
          // cannot succeed, so stop and ask the agent to connect again.
          onReject: () => {
            this._setState(PhoneState.FAILED);
            this._fail("The phone system rejected the login. Connect the phone again.");
          },
        },
      });
      this._reconnectDelay = RECONNECT_BASE_MS;
    } catch (err) {
      this._setState(PhoneState.FAILED);
      this._fail("Could not reach the phone system. Retrying.");
      this._scheduleReconnect();
    }
  }

  _registration(sipState) {
    const { RegistererState } = this.SIP;
    if (sipState === RegistererState.Registered) {
      if (this._state !== PhoneState.ON_CALL && this._state !== PhoneState.RINGING) {
        this._setState(PhoneState.READY);
      }
      this._scheduleReRegister();
    } else if (sipState === RegistererState.Unregistered) {
      if (
        !this._stopping &&
        this._state !== PhoneState.ON_CALL &&
        this._state !== PhoneState.FAILED
      ) {
        this._setState(PhoneState.CONNECTING);
      }
    }
  }

  _scheduleReRegister() {
    clearTimeout(this._registerTimer);
    // Renew before the contact expires, not after.
    const expires = this._registerer?.configuration?.expires || 600;
    const delay = Math.max(30000, expires * 1000 - RE_REGISTER_MARGIN_MS);
    this._registerTimer = setTimeout(() => {
      if (this._stopping || !this._registerer) return;
      this._registerer.register().catch(() => {
        this._fail("Lost registration. Reconnecting.");
        this._scheduleReconnect();
      });
    }, delay);
  }

  _dropped() {
    if (this._stopping) return;
    // A dropped socket during a call does not end the call - media flows
    // separately - but no new call can arrive, so the agent should know.
    this._fail("Connection to the phone system lost. Reconnecting.");
    if (this._state !== PhoneState.ON_CALL) this._setState(PhoneState.CONNECTING);
    this._scheduleReconnect();
  }

  _scheduleReconnect() {
    if (this._stopping) return;
    clearTimeout(this._reconnectTimer);
    const jitter = Math.random() * 0.3 + 0.85;
    const delay = Math.round(this._reconnectDelay * jitter);
    this._reconnectTimer = setTimeout(async () => {
      await this._teardownUa();
      await this._open();
    }, delay);
    this._reconnectDelay = Math.min(this._reconnectDelay * 2, RECONNECT_MAX_MS);
  }

  // ---------- incoming (the engine ringing the agent leg) ----------

  _receive(invitation) {
    if (this._session) {
      // Already talking. Busy is the honest answer - the engine records it
      // as busy rather than as a call the agent ignored.
      try {
        invitation.reject({ statusCode: 486 });
      } catch (e) {
        /* already gone */
      }
      return;
    }

    this._session = invitation;
    this._muted = false;
    this._setState(PhoneState.RINGING);
    this._bindSession(invitation);
    this.onCall({ event: "ringing" });
    if (this.autoAnswer) this.answer();
  }

  /** Accept the engine's ring. Call from a click so audio can autoplay. */
  answer() {
    const session = this._session;
    if (!session || this._state !== PhoneState.RINGING) return false;
    session
      .accept({ sessionDescriptionHandlerOptions: { constraints: AUDIO_ONLY } })
      .catch(() => {
        // Almost always a microphone permission the agent has not granted.
        this._fail("Could not answer - the browser blocked microphone access.");
        this._session = null;
        this._setState(PhoneState.READY);
        this.onCall({ event: "ended" });
      });
    return true;
  }

  /** Decline the engine's ring. */
  reject() {
    const session = this._session;
    if (!session || this._state !== PhoneState.RINGING) return false;
    try {
      session.reject({ statusCode: 486 });
    } catch (e) {
      return false;
    }
    return true;
  }

  _bindSession(session) {
    const { SessionState } = this.SIP;
    session.stateChange.addListener((s) => {
      if (s === SessionState.Established) {
        this._setState(PhoneState.ON_CALL);
        this._attachAudio(session);
        this._watchQuality(session);
        this.onCall({ event: "connected" });
      }
      if (s === SessionState.Terminated && this._session === session) {
        this._endCall();
      }
    });
  }

  _attachAudio(session) {
    const pc = session.sessionDescriptionHandler.peerConnection;
    const stream = new MediaStream();
    pc.getReceivers().forEach((r) => {
      if (r.track) stream.addTrack(r.track);
    });

    if (!this._audio) {
      this._audio = document.createElement("audio");
      this._audio.autoplay = true;
      this._audio.style.display = "none";
      document.body.appendChild(this._audio);
    }
    this._audio.srcObject = stream;
    this._audio.play().catch(() => {
      // Autoplay policy: the agent hears nothing and has no idea why.
      this._fail("Click anywhere on the page to enable call audio.");
    });
  }

  _watchQuality(session) {
    clearInterval(this._statsTimer);
    const pc = session.sessionDescriptionHandler.peerConnection;
    let lastLost = 0;
    let lastReceived = 0;

    this._statsTimer = setInterval(async () => {
      let report;
      try {
        report = await pc.getStats(null);
      } catch (e) {
        return;
      }

      report.forEach((r) => {
        if (r.type !== "inbound-rtp" || r.kind !== "audio") return;
        const lost = (r.packetsLost || 0) - lastLost;
        const received = (r.packetsReceived || 0) - lastReceived;
        lastLost = r.packetsLost || 0;
        lastReceived = r.packetsReceived || 0;

        if (received <= 0) return;
        const ratio = lost / (lost + received);
        this.onQuality({ lossRatio: ratio, poor: ratio > POOR_AUDIO_LOSS_RATIO });
      });
    }, STATS_INTERVAL_MS);
  }

  _endCall() {
    clearInterval(this._statsTimer);
    this._statsTimer = null;
    this._session = null;
    this._muted = false;
    if (this._audio) this._audio.srcObject = null;
    const registered = this._registerer?.state === this.SIP.RegistererState.Registered;
    if (registered) this._setState(PhoneState.READY);
    else this._setState(this._ua ? PhoneState.CONNECTING : PhoneState.OFFLINE);
    this.onCall({ event: "ended" });
  }

  // ---------- controls ----------

  /**
   * Mute the agent's microphone locally. This is the agent covering their
   * own mic for a moment; it is not recorded on the call.
   */
  setMuted(muted) {
    const pc = this._session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return false;
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === "audio") {
        sender.track.enabled = !muted;
      }
    });
    this._muted = !!muted;
    this.onCall({ event: "muted", muted: this._muted });
    return true;
  }

  toggleMute() {
    return this.setMuted(!this._muted);
  }

  /**
   * Replace the relay credential without dropping the registration, from
   * GET /dialer/softphone/ice-servers/. Takes effect on the next call; a
   * call in progress keeps the relay it allocated.
   */
  setIceServers(servers, expiresAt) {
    if (!this._sdhOptions) return false;
    this._sdhOptions.peerConnectionConfiguration.iceServers = (servers || []).slice();
    this._scheduleIceRefresh(expiresAt);
    return true;
  }

  _scheduleIceRefresh(expiresAt) {
    clearTimeout(this._iceTimer);
    if (!expiresAt || typeof this.onIceExpiring !== "function") return;
    // Ask early: a credential refreshed on the expiry itself leaves a window
    // where coturn has already stopped accepting it.
    const ms = expiresAt * 1000 - Date.now() - ICE_REFRESH_MARGIN_MS;
    this._iceTimer = setTimeout(() => {
      if (!this._stopping) this.onIceExpiring();
    }, Math.max(30000, ms));
  }

  /**
   * Browser-side hang-up. The dashboard's Hang Up goes through Django
   * (DELETE /dialer/calls/<id>/hangup/); this is only for a ring or call
   * that has no call_id to hang up with.
   */
  hangup() {
    const session = this._session;
    if (!session) return false;
    try {
      if (session.state === this.SIP.SessionState.Established) {
        session.bye();
      } else {
        session.reject({ statusCode: 486 });
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  /** DTMF, for IVR menus and extension prompts. */
  sendDigit(digit) {
    if (!this._session || !/^[0-9*#]$/.test(String(digit))) return false;
    try {
      return this._session.sessionDescriptionHandler.sendDtmf(String(digit));
    } catch (e) {
      return false;
    }
  }

  // ---------- shutdown ----------

  async _teardownUa() {
    clearTimeout(this._registerTimer);
    clearInterval(this._statsTimer);
    if (this._registerer) {
      try {
        await this._registerer.unregister();
      } catch (e) {
        /* the socket may already be gone */
      }
      this._registerer = null;
    }
    if (this._ua) {
      try {
        await this._ua.stop();
      } catch (e) {
        /* already stopped */
      }
      this._ua = null;
    }
  }

  /** Full stop. Call on Disconnect Phone, logout and page unload. */
  async disconnect() {
    this._stopping = true;
    clearTimeout(this._reconnectTimer);
    clearTimeout(this._iceTimer);
    if (this._session) {
      try {
        this._session.bye();
      } catch (e) {
        /* not established */
      }
      this._session = null;
    }
    await this._teardownUa();
    // The credential goes with the connection. Nothing is persisted.
    this._credential = null;
    this._sdhOptions = null;
    if (this._audio) {
      this._audio.srcObject = null;
      this._audio.remove();
      this._audio = null;
    }
    this._setState(PhoneState.OFFLINE);
  }
}
